import { useCallback, useEffect, useRef, useState } from 'react';
import { Preloader, Toggle } from 'konsta/react';
import { Check, ChevronRight, Copy, ImagePlus, Pencil, Send, Trash2, UserPlus } from 'lucide-react';
import type { I18nKey } from '@harmony/shared';
import {
  api,
  actionId,
  type AgentMe,
  type AgentPeriod,
  type AgentProvider,
  type AgentProviderDetail,
  type AgentRequest,
  type AgentStats,
  type AgentSubscription,
  type EditSuggestion,
} from '../../api';
import { Avatar, BottomSheet, EmptyState, PrimaryButton, Rating, TopBar, etb, timeLabel, useToast } from '../../components/ui';
import { inputClass } from '../../components/form';
import { ProfileBody } from '../ProviderProfile';
import { StatusBadge } from '../Bookings';
import { confirmDialog, haptic, tg } from '../../telegram';
import { useNav } from '../../nav';
import { useErrorText, useLang, useName, useT } from '../../i18n';

const PSTATUS_STYLE: Record<string, string> = {
  active: 'bg-green-500/15 text-green-700',
  inactive: 'bg-tg-secondary text-tg-hint',
  pending: 'bg-amber-500/15 text-amber-700',
  denied: 'bg-red-500/10 text-red-600',
  draft: 'bg-tg-secondary text-tg-hint',
};

function shareLink(link: string) {
  const url = `https://t.me/share/url?url=${encodeURIComponent(link)}`;
  if (tg?.openTelegramLink) tg.openTelegramLink(url);
  else window.open(url, '_blank');
  api.agentLinkShared().catch(() => undefined);
}

/** The agent's code and the invite link they share with providers. */
export function AgentCard({ compact }: { compact?: boolean }) {
  const t = useT();
  const [agent, setAgent] = useState<AgentMe | null>(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    api.agentMe().then(setAgent).catch(() => undefined);
  }, []);
  if (!agent) return null;
  const copy = async () => {
    await navigator.clipboard?.writeText(agent.inviteLink).catch(() => undefined);
    haptic('success');
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="mx-3 mt-3 rounded-2xl bg-tg-secondary p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5 text-sm text-tg-hint">
            <UserPlus size={15} /> {compact ? t('agent.add_provider') : t('agent.your_code')}
          </div>
          <div className="text-2xl font-bold tracking-wider">{agent.agentCode}</div>
        </div>
        <button onClick={copy} className="flex items-center gap-1 rounded-full bg-tg-bg px-3 py-1.5 text-sm text-tg-link">
          {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? t('agent.copied') : t('invite.copy')}
        </button>
      </div>
      <p className="mb-3 mt-1 text-sm text-tg-hint">{t('agent.add_hint')}</p>
      <PrimaryButton onClick={() => shareLink(agent.inviteLink)}>
        <Send size={17} /> {t('agent.share')}
      </PrimaryButton>
    </div>
  );
}

function Segments<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: Array<[T, string]> }) {
  return (
    <div className="no-scrollbar flex gap-2 overflow-x-auto px-3 py-2.5">
      {options.map(([v, label]) => (
        <button
          key={v}
          onClick={() => {
            haptic('select');
            onChange(v);
          }}
          className={`shrink-0 rounded-full px-4 py-2 text-sm font-medium ${value === v ? 'bg-tg-text text-tg-bg' : 'bg-tg-secondary'}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function StatTile({ label, value, tone }: { label: string; value: number | string; tone?: string }) {
  return (
    <div className="rounded-2xl bg-tg-secondary p-3">
      <div className={`text-2xl font-bold ${tone ?? ''}`}>{value}</div>
      <div className="text-xs text-tg-hint">{label}</div>
    </div>
  );
}

// ─────────────── My Providers tab ───────────────

export function MyProvidersTab() {
  const t = useT();
  const nav = useNav();
  const [items, setItems] = useState<AgentProvider[] | null>(null);
  const [month, setMonth] = useState<AgentStats | null>(null);
  const load = useCallback(() => {
    api.agentProviders().then(setItems).catch(() => setItems([]));
    api.agentStats('month').then(setMonth).catch(() => undefined);
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  if (!items) return <div className="flex justify-center pt-24"><Preloader /></div>;
  const open = (p: AgentProvider) => nav.push(`agent-provider:${p.id}`, <ManageProviderPage id={p.id} onChanged={load} />);

  return (
    <div className="pb-28">
      {month && (
        <div className="grid grid-cols-3 gap-2 px-3 pt-3">
          <StatTile label={t('agent.stat.active')} value={month.activeProviders} />
          <StatTile label={`${t('agent.stat.received')} · ${t('agent.period.month')}`} value={month.received} />
          <StatTile label={t('agent.stat.accepted')} value={month.accepted} tone="text-green-700" />
        </div>
      )}
      <AgentCard compact />
      {!items.length ? (
        <EmptyState icon="👥" text={t('agent.no_providers')} />
      ) : (
        <div className="mx-3 mt-3 divide-y divide-black/5 overflow-hidden rounded-2xl bg-tg-secondary">
          {items.map((p) => (
            <button key={p.id} onClick={() => open(p)} className="flex w-full items-center gap-3 px-3 py-3 text-left active:bg-black/5">
              <Avatar src={p.photo} name={p.name} size={48} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-semibold">{p.name}</span>
                  {p.available && p.status === 'active' && <span className="h-2 w-2 shrink-0 rounded-full bg-green-500" />}
                </div>
                <div className="flex flex-wrap items-center gap-x-2 text-sm text-tg-hint">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${PSTATUS_STYLE[p.status]}`}>{t(`agent.pstatus.${p.status}` as I18nKey)}</span>
                  <span>{t('agent.requests_count', { n: p.requestsThisMonth })}</span>
                </div>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1">
                <Rating avg={p.ratingAvg} count={p.ratingCount} />
                {p.editPending && <Pencil size={14} className="text-amber-600" />}
              </div>
              <ChevronRight size={18} className="shrink-0 text-tg-hint" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** One provider: how clients see them, this month's numbers, Active switch, suggest changes, remove. */
function ManageProviderPage({ id, onChanged }: { id: string; onChanged: () => void }) {
  const t = useT();
  const nav = useNav();
  const toast = useToast();
  const errText = useErrorText();
  const [p, setP] = useState<AgentProviderDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [suggest, setSuggest] = useState(false);
  const load = useCallback(() => {
    api.agentProvider(id).then(setP).catch(() => toast(t('err.NOT_FOUND')));
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);

  if (!p)
    return (
      <div className="h-full">
        <TopBar />
        <div className="flex justify-center pt-24"><Preloader /></div>
      </div>
    );

  const canToggle = (p.status === 'active' || p.status === 'inactive') && p.inactiveBy !== 'ADMIN';
  const toggle = async () => {
    setBusy(true);
    try {
      await api.agentSetActive(p.id, p.status !== 'active');
      haptic('success');
      load();
      onChanged();
    } catch (e) {
      toast(errText(e));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!(await confirmDialog(t('agent.remove_confirm', { name: p.name })))) return;
    try {
      await api.agentRemove(p.id);
      haptic('success');
      toast(t('agent.removed'));
      onChanged();
      nav.pop();
    } catch (e) {
      toast(errText(e));
    }
  };

  return (
    <div className="flex h-full flex-col">
      <TopBar title={p.name} />
      <div className="flex-1 overflow-y-auto pb-10">
        <div className="grid grid-cols-3 gap-2 px-3 pt-3">
          <StatTile label={t('agent.stat.received')} value={p.month.received} />
          <StatTile label={t('agent.stat.accepted')} value={p.month.accepted} tone="text-green-700" />
          <StatTile label={t('agent.stat.expired')} value={p.month.expired} tone={p.month.expired ? 'text-amber-600' : ''} />
        </div>
        <p className="px-4 pt-1 text-xs text-tg-hint">{t('agent.period.month')}</p>

        {canToggle && (
          <div className="mx-3 mt-3 flex items-center gap-3 rounded-2xl bg-tg-secondary p-4">
            <div className="flex-1 font-medium">{t('agent.set_active')}</div>
            <Toggle checked={p.status === 'active'} onChange={toggle} disabled={busy} />
          </div>
        )}
        {!canToggle && (
          <p className="mx-3 mt-3 rounded-2xl bg-tg-secondary p-3 text-sm text-tg-hint">
            <span className={`mr-2 rounded-full px-2 py-0.5 text-xs font-semibold ${PSTATUS_STYLE[p.status]}`}>{t(`agent.pstatus.${p.status}` as I18nKey)}</span>
            {p.inactiveBy === 'ADMIN' && t('err.INACTIVE_BY_ADMIN')}
          </p>
        )}

        {p.card && (
          <div className="mt-3 px-3">
            {p.pendingSuggestion ? (
              <p className="rounded-2xl bg-amber-400/15 p-3 text-sm">✏️ {t('agent.suggest_pending', { name: p.name })}</p>
            ) : (
              <PrimaryButton tone="soft" onClick={() => setSuggest(true)}>
                <Pencil size={17} /> {t('agent.suggest_edit')}
              </PrimaryButton>
            )}
          </div>
        )}

        {p.card && (
          <div className="mt-4">
            <ProfileBody p={p.card} canBook={false} />
          </div>
        )}

        <div className="mt-6 px-3">
          <button onClick={remove} className="flex w-full items-center justify-center gap-2 rounded-2xl py-3 font-semibold text-red-600 active:bg-tg-secondary">
            <Trash2 size={17} /> {t('agent.remove')}
          </button>
        </div>
      </div>
      {p.card && (
        <SuggestSheet
          open={suggest}
          p={p}
          onClose={() => setSuggest(false)}
          onSent={() => {
            setSuggest(false);
            toast(t('agent.suggest_sent', { name: p.name }));
            load();
            onChanged();
          }}
        />
      )}
    </div>
  );
}

function SuggestSheet({ open, p, onClose, onSent }: { open: boolean; p: AgentProviderDetail; onClose: () => void; onSent: () => void }) {
  const t = useT();
  const errText = useErrorText();
  const card = p.card!;
  const [name, setName] = useState(card.displayName);
  const [bio, setBio] = useState(card.bio ?? '');
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    setName(card.displayName);
    setBio(card.bio ?? '');
    setPrices(Object.fromEntries(card.prices.map((x) => [String(x.minutes), String(x.price)])));
    setError(null);
  }, [open]);

  const send = async () => {
    const changes: EditSuggestion = {};
    if (name.trim() && name.trim() !== card.displayName) changes.displayName = name.trim();
    if (bio.trim() !== (card.bio ?? '')) changes.bio = bio.trim();
    const changedPrices: Record<string, number> = {};
    for (const x of card.prices) {
      const v = Number(prices[String(x.minutes)]);
      if (Number.isInteger(v) && v > 0 && v !== x.price) changedPrices[String(x.minutes)] = v;
    }
    if (Object.keys(changedPrices).length) changes.prices = changedPrices;
    if (!Object.keys(changes).length) return setError(t('err.NOTHING_TO_CHANGE'));
    setBusy(true);
    setError(null);
    try {
      await api.agentSuggest(p.id, changes);
      haptic('success');
      onSent();
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <BottomSheet open={open} onClose={onClose}>
      <h3 className="mb-4 text-xl font-bold">{t('agent.suggest_edit')}</h3>
      <label className="mb-1 block text-sm text-tg-hint">{t('edit.field.displayName')}</label>
      <input className={inputClass} value={name} maxLength={40} onChange={(e) => setName(e.target.value)} />
      <label className="mb-1 mt-3 block text-sm text-tg-hint">{t('edit.field.bio')}</label>
      <textarea className={inputClass + ' min-h-[70px] resize-none'} value={bio} maxLength={70} onChange={(e) => setBio(e.target.value)} />
      <div className="mt-3 grid grid-cols-2 gap-2">
        {card.prices.map((x) => (
          <div key={x.minutes}>
            <label className="mb-1 block text-sm text-tg-hint">{t('provider.min', { minutes: x.minutes })} (ETB)</label>
            <input
              className={inputClass}
              inputMode="numeric"
              value={prices[String(x.minutes)] ?? ''}
              onChange={(e) => setPrices((s) => ({ ...s, [String(x.minutes)]: e.target.value.replace(/\D/g, '') }))}
            />
          </div>
        ))}
      </div>
      {error && <p className="mt-3 rounded-xl bg-red-500/10 p-3 text-red-600">{error}</p>}
      <PrimaryButton className="mt-4" disabled={busy} onClick={send}>
        {busy ? <Preloader className="h-5 w-5" /> : t('agent.suggest_send')}
      </PrimaryButton>
    </BottomSheet>
  );
}

// ─────────────── Requests tab ───────────────

export function AgentRequestsTab() {
  const t = useT();
  const lang = useLang();
  const name = useName();
  const [period, setPeriod] = useState<AgentPeriod>('week');
  const [stats, setStats] = useState<AgentStats | null>(null);
  const [items, setItems] = useState<AgentRequest[] | null>(null);
  useEffect(() => {
    setItems(null);
    api.agentStats(period).then(setStats).catch(() => undefined);
    api.agentRequests(period).then(setItems).catch(() => setItems([]));
  }, [period]);

  return (
    <div className="pb-28">
      <Segments
        value={period}
        onChange={setPeriod}
        options={[
          ['today', t('agent.period.today')],
          ['week', t('agent.period.week')],
          ['month', t('agent.period.month')],
          ['last_month', t('agent.period.last_month')],
        ]}
      />
      {stats && (
        <div className="grid grid-cols-3 gap-2 px-3">
          <StatTile label={t('agent.stat.received')} value={stats.received} />
          <StatTile label={t('agent.stat.accepted')} value={stats.accepted} tone="text-green-700" />
          <StatTile label={t('agent.stat.completed')} value={stats.completed} tone="text-green-700" />
          <StatTile label={t('agent.stat.declined')} value={stats.declined} tone={stats.declined ? 'text-red-600' : ''} />
          <StatTile label={t('agent.stat.expired')} value={stats.expired} tone={stats.expired ? 'text-amber-600' : ''} />
          <StatTile label={t('agent.stat.new')} value={stats.newProviders} />
        </div>
      )}
      {!items ? (
        <div className="flex justify-center pt-12"><Preloader /></div>
      ) : !items.length ? (
        <EmptyState icon="📭" text={t('requests.empty')} />
      ) : (
        <div className="mx-3 mt-3 divide-y divide-black/5 overflow-hidden rounded-2xl bg-tg-secondary">
          {items.map((r) => (
            <div key={r.id} className="px-3 py-3">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate font-semibold">{r.provider.name}</span>
                <StatusBadge s={r.status} />
              </div>
              <div className="text-sm text-tg-hint">
                {t('booking.session', { minutes: r.minutes, price: etb(r.price) })} · {timeLabel(r.requestedAt, lang)}
                {(r.area || r.city) && <> · {name(r.area ?? r.city)}</>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────── Subscription ───────────────

/** Shown on the agent's Profile when the admin switched agent subscriptions on. */
export function SubscriptionCard() {
  const t = useT();
  const lang = useLang();
  const errText = useErrorText();
  const toast = useToast();
  const [s, setS] = useState<AgentSubscription | null>(null);
  const [paying, setPaying] = useState(false);
  const [instructions, setInstructions] = useState('');
  const [evidence, setEvidence] = useState<{ key: string; preview: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const key = useRef(actionId());
  const file = useRef<HTMLInputElement>(null);
  const load = () => api.agentSubscription().then(setS).catch(() => undefined);
  useEffect(() => {
    load();
  }, []);
  if (!s?.enabled || s.amount <= 0) return null;
  const until = s.paidUntil ? new Date(s.paidUntil).toLocaleDateString(lang === 'am' ? 'am-ET' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : null;

  return (
    <div className="mx-3 mt-3 rounded-2xl bg-tg-secondary p-4">
      <div className="mb-1 flex items-center justify-between">
        <span className="font-semibold">{t('agent.subscription_title')}</span>
        <span
          className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
            s.waiting ? 'bg-amber-500/15 text-amber-700' : s.due ? 'bg-red-500/10 text-red-600' : 'bg-green-500/15 text-green-700'
          }`}
        >
          {s.waiting ? t('agent.subscription_pending') : s.due ? t('agent.subscription_due') : t('agent.subscription_paid', { date: until ?? '' })}
        </span>
      </div>
      <p className="text-sm text-tg-hint">
        {t('agent.subscription_body', { count: s.providerCount, price: etb(s.pricePerProvider), amount: etb(s.amount), days: s.periodDays })}
      </p>
      {s.due && !s.waiting && (
        <PrimaryButton
          className="mt-3"
          onClick={() => {
            key.current = actionId();
            setEvidence(null);
            api.paymentInstructions().then((r) => setInstructions(r.text)).catch(() => undefined);
            setPaying(true);
          }}
        >
          {t('agent.subscription_pay')}
        </PrimaryButton>
      )}
      <BottomSheet open={paying} onClose={() => setPaying(false)}>
        <h3 className="mb-2 text-xl font-bold">{t('agent.subscription_title')}</h3>
        <div className="mb-3 flex items-center justify-between rounded-2xl bg-tg-secondary px-4 py-3">
          <span className="text-tg-hint">{t('pay.total')}</span>
          <span className="text-2xl font-bold">{etb(s.amount)} ETB</span>
        </div>
        <div className="rounded-2xl border border-black/10 p-4">
          <h4 className="mb-1 font-semibold">{t('pay.how_title')}</h4>
          <p className="whitespace-pre-wrap text-[15px]">{instructions}</p>
        </div>
        <input
          ref={file}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={async (e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            setBusy(true);
            try {
              const r = await api.upload('payment_evidence', f, f.name);
              setEvidence({ key: r.key, preview: URL.createObjectURL(f) });
            } catch (err) {
              toast(errText(err));
            } finally {
              setBusy(false);
            }
          }}
        />
        <button onClick={() => file.current?.click()} className="mt-3 flex w-full items-center gap-3 rounded-2xl border-2 border-dashed border-black/15 p-3 text-left">
          {evidence ? (
            <img src={evidence.preview} alt="" className="h-16 w-16 rounded-xl object-cover" />
          ) : (
            <span className="flex h-16 w-16 items-center justify-center rounded-xl bg-tg-secondary text-tg-hint">
              <ImagePlus size={26} />
            </span>
          )}
          <span className="font-medium">{evidence ? t('pay.uploaded') : t('pay.upload')}</span>
        </button>
        <PrimaryButton
          className="mt-4"
          disabled={!evidence || busy}
          onClick={async () => {
            setBusy(true);
            try {
              await api.pay({ purpose: 'AGENT_SUBSCRIPTION', evidenceKey: evidence!.key, idempotencyKey: key.current });
              haptic('success');
              toast(t('pay.sent_title'));
              setPaying(false);
              load();
            } catch (err) {
              toast(errText(err));
            } finally {
              setBusy(false);
            }
          }}
        >
          {t('pay.submit')}
        </PrimaryButton>
      </BottomSheet>
    </div>
  );
}
